import { h } from 'preact';
import { useState, useRef, useEffect } from 'preact/hooks';
import Image from '../utils/image';
import Price from '../product/product-price';
import PriceRange from './price-range';
import CustomizeButton from '../product/customize-button';
import ProductCardBadge from './product-card-badge';

const ProductCard = ({ data, key, reviewStars = true }) => {
  const cardRef = useRef(null);
  const [currentProduct, setCurrentProduct] = useState(data.products[0]);
  const currentVariant = currentProduct.variants[0];
  const productId = parseInt(
    (currentProduct?.id || '').split('gid://shopify/Product/')[1],
    10,
  );

  // find the product that has the tag cstm: , but the tag is not cstm:personalization
  const hasCustomizationTag = data.products.some((product) =>
    product.tags.some(
      (tag) => tag.includes('cstm:') && !tag.includes('cstm:personalization'),
    ),
  );

  useEffect(() => {
    const cardElement = cardRef.current;
    if (!cardElement) return;

    const yotpoContainer = cardElement.querySelector('.yotpo-widget-container');
    if (!yotpoContainer) return;

    const observer = new MutationObserver((mutationsList, observer) => {
      for (const mutation of mutationsList) {
        if (
          mutation.type === 'childList' &&
          yotpoContainer.querySelector(
            '.yotpo-widget-instance .yotpo-bottom-line-scroll-panel',
          )
        ) {
          observer.disconnect();
          break;
        }
      }
    });

    observer.observe(yotpoContainer, {
      childList: true,
      subtree: true,
    });

    // eslint-disable-next-line consistent-return
    return () => observer.disconnect();
  }, [productId]);

  const handleProductClick = (e, index) => {
    e.preventDefault();
    setCurrentProduct(data.products[index]);

    if (window.yotpo && reviewStars) {
      const cardElement = cardRef.current;
      if (!cardElement) return;

      const yotpoContainer = cardElement.querySelector(
        '.yotpo-widget-container',
      );

      yotpoContainer
        .querySelectorAll('.yotpo-widget-instance')
        .forEach((widget) => widget.remove());

      // Create a new Yotpo widget element
      const newWidget = document.createElement('div');
      newWidget.className = 'hidden yotpo-widget-instance';
      newWidget.setAttribute('data-yotpo-instance-id', '407152');
      newWidget.setAttribute(
        'data-yotpo-product-id',
        data.products[index].id.split('gid://shopify/Product/')[1],
      );
      newWidget.setAttribute('data-yotpo-section-id', 'product');
      yotpoContainer.appendChild(newWidget);

      yotpoContainer
        .querySelector('.yotpo-widget-instance')
        .classList.remove('hidden');

      // Initialize Yotpo only for the new widget
      window.yotpo.initWidgets([{ domElement: newWidget }]);
    }
  };

  const pricesArr =
    data.products.length > 1 &&
    data.products.map((product) => Number(product.variants[0].priceV2));

  const comparePricesArr =
    data.products.length > 1 &&
    data.products
      .map((product) => product.variants[0].compareAtPrice)
      .filter((p) => p);

  const content = (
    <div className="flex flex-col px-[30px] pt-3.5 pb-4" ref={cardRef}>
      <ProductCardBadge
        currentProduct={currentProduct}
        currentVariant={currentVariant}
      />
      <h3 className="font-m-18 font-bold uppercase mb-1.5 laptop:text-[22px]">
        {currentProduct.title}
      </h3>
      <div className="flex flex-wrap items-center">
        {data.products.length === 1 && (
          <Price
            currentPrice={data.products[0].variants[0].priceV2}
            currentComparePrice={data.products[0].variants[0].compareAtPrice}
            classes={'font-m-12 tablet:font-m-14'}
          />
        )}
        {data.products.length > 1 && (
          <PriceRange
            pricesArr={pricesArr}
            comparePricesArr={comparePricesArr}
          />
        )}
        {hasCustomizationTag && <CustomizeButton classes="ml-2" />}
      </div>
      {reviewStars && (
        <div className="yotpo-widget-container overflow-hidden">
          <div
            className="yotpo-widget-instance"
            data-yotpo-instance-id="407152"
            data-yotpo-product-id={productId}
            data-yotpo-section-id="product"
          />
        </div>
      )}
    </div>
  );

  return (
    <a
      href={`/products/${currentProduct.handle}`}
      className="flex flex-col h-full mb-5 bg-bg-6 tablet:mb-0 product-card"
      key={key}
    >
      <div className="relative">
        {/* Temporarily fix this to have both pb classes to deal with a Searchspring bug */}
        <div className="relative h-0 pb-[62%] pb-[68%] w-full overflow-hidden">
          {currentProduct.horizontalImage && (
            <Image
              src={
                currentProduct.horizontalImage
                  ? currentProduct.horizontalImage
                  : currentProduct.images[0].src
              }
              className="absolute top-0 left-0 block object-contain w-full h-full laptop:ml-2"
              srcWidths={{
                sm: 720,
                md: 800,
                lg: 920,
                xl: 1000,
                '2xl': 1120,
              }}
              alt={`${currentProduct.title} image`}
            />
          )}
        </div>
        <div
          className={`min-h-[38px] laptop:absolute laptop:top-0 flex px-[30px] laptop:flex-col laptop:p-[30px] laptop:overflow-y-auto laptop:max-h-[100%] scrollbar-hide ${
            !data.products.length ? 'h-[38px]' : ''
          }`}
        >
          {data.products.length > 1 &&
            data.products.map((product, index) => (
              <button
                className={`relative block w-[45px] h-0 pb-[13.5%] border-b laptop:pb-0 laptop:h-[36px] laptop:min-h-[36px] laptop:pl-[7px] laptop:border-b-0 laptop:border-l overflow-hidden ${
                  product.handle === currentProduct.handle
                    ? 'border-accent-1'
                    : 'border-grey-400'
                }`}
                key={product.handle}
                onClick={(e) => handleProductClick(e, index)}
              >
                {product.horizontalImage && (
                  <Image
                    src={product.horizontalImage}
                    className="absolute block object-contain w-full h-auto -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2 laptop:static laptop:translate-x-0 laptop:translate-y-0"
                    width={45}
                    height={36}
                    alt={`${product.title} image`}
                    srcWidths={{
                      sm: 45,
                      md: 45,
                      lg: 45,
                      xl: 45,
                      '2xl': 45,
                    }}
                  />
                )}
              </button>
            ))}
        </div>
      </div>
      {content}
    </a>
  );
};

export default ProductCard;
